import { toneFor, remainingText, remedyFor, type Limit } from '../lib/limits'
export { mb, remainingText, toneFor, remedyFor } from '../lib/limits'
export type { Limit, LimitKey, Tone } from '../lib/limits'

/**
 * One meter for every limit that is not the person cap (v0.25.0, D-144).
 *
 * `CapacityMeter` proved the shape for people-in-a-tree and it is left alone.
 * This is its sibling for the four limits that never got one: constellations,
 * storage, Sage messages, and open support reports.
 *
 * Three things it does differently, each learned from the audits:
 *
 *  · **It leads with what is LEFT, not what is used.** "183 of 250 MB used" makes
 *    the reader do arithmetic to answer the question they actually have, which is
 *    whether the thing in their hand will fit.
 *
 *  · **The warning threshold is derived, not a flat percentage.** For storage the
 *    band opens when the remaining space is less than twice the largest single
 *    file the plan allows — that stays correct after a grant changes the
 *    denominator, and it stays correct across plans where 75% would warn far too
 *    late. For counted limits the flat rule is right and is kept.
 *
 *  · **It never goes fully silent for storage.** Nobody asks how many people are
 *    in a tree before typing a name, but everyone wonders whether a four-minute
 *    video will fit. A quiet permanent line costs nothing and is the cheapest
 *    improvement available.
 *
 * The remedy line is the point of the whole component. Every limit in this app
 * used to refuse people with advice they could not act on, so `purchases_enabled`
 * comes from the live Stripe key: when money cannot be taken, the meter says to
 * ask the keeper, which is now a thing the keeper can actually do.
 */

export default function LimitMeter({ limit, plan = 'seedling', purchasesEnabled = false, compact = false }: {
  limit: Limit
  plan?: string
  purchasesEnabled?: boolean
  compact?: boolean
}) {
  if (limit.limit < 0) return null
  const tone = toneFor(limit, plan)
  const pct = Math.min(100, Math.round((limit.used / Math.max(1, limit.limit)) * 100))

  // quiet: a number, not a panel. Storage always shows one; the counted limits
  // stay out of the way until they are worth mentioning.
  if (tone === 'quiet') {
    if (compact || limit.unit === 'bytes') {
      return <span className="text-[11px] text-slate-600">· {remainingText(limit)}</span>
    }
    return null
  }

  const skin = tone === 'full'
    ? 'border-red-500/35 bg-red-500/[0.07] text-red-200'
    : 'border-amber-500/30 bg-amber-500/[0.06] text-amber-200'

  return (
    <div className={`flex items-start gap-2.5 mt-2 mb-1 px-3 py-2 rounded-xl border text-xs ${skin}`}>
      <span className="text-sm">{tone === 'full' ? '🚫' : '⚠'}</span>
      <div className="flex-1">
        <p>
          <b>{tone === 'full' ? 'No room left' : 'Running low'} — {remainingText(limit)}.</b>{' '}
          {remedyFor(limit, purchasesEnabled)}
        </p>
        {/* a bar with one reachable value communicates nothing: on a cap of 1 it is
            only ever drawn full. Same threshold as the two-slot warning rule. */}
        {limit.limit >= 4 && (
          <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden mt-1.5">
            <div className={`h-full ${tone === 'full' ? 'bg-red-500/70' : 'bg-amber-500/70'}`} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}
